import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { LucideZap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useGlobalStoreActions,
  useSubscriptionModal,
} from "@/store/global-store";
import { useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api-service";
import { SubscriptionModal } from "@/components/subscription-modal";
import { createFileRoute } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/store/useAuthStore";

export const Route = createFileRoute("/app/_layout/usage-stats")({
  component: UsageStats,
});

// Card title mapping
const featureTitles = {
  ai_drafting: "AI Drafting",
  voice_transcription: "Voice Transcription",
  ai_chat: "AI Chat",
  personalization_document: "Personalization Documents",
  project_context: "Project Context",
};

// Card description mapping
const featureDescriptions = {
  ai_drafting: "Characters generated with AI assistance",
  voice_transcription: "Minutes of audio transcribed",
  ai_chat: "AI chat messages used",
  personalization_document: "Your documents used in personalizing",
  project_context: "Total contexts attached to projects",
};

// Card unit mapping
const featureUnits = {
  ai_drafting: "words",
  voice_transcription: "secs",
  ai_chat: "tokens",
  personalization_document: "",
  project_context: "",
};

// Helper function to calculate usage boundary
const usageBoundary = (usage: number, limit: number) => {
  const percentage = Math.min(Math.round((usage / limit) * 100), 100);
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  return {
    isAtLimit,
    isNearLimit,
    percentage,
  };
};

// UsageCard component for individual feature card
function UsageCard({
  title,
  description,
  current,
  max,
  isPremium,
  unit,
  feature,
}: {
  title: string;
  description: string;
  current: number;
  max: number;
  isPremium: boolean;
  unit: string;
  feature: string;
}) {
  const { isAtLimit, isNearLimit, percentage } = usageBoundary(current, max);

  return (
    <Card className="overflow-hidden relative transform transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div>
              <CardTitle className="text-sm flex items-center">
                {title}
                <span className="absolute text-neutral-600 text-xs top-2 right-2">
                  {title === "AI Chat"
                    ? "/day"
                    : title === "Personalization Documents"
                      ? ""
                      : "/month"}
                </span>
              </CardTitle>
              <CardDescription className="mt-1 font-satoshi">
                {description}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Progress
            value={percentage}
            className={`h-2 ${
              isPremium
                ? "bg-gray-200 dark:bg-gray-700"
                : isAtLimit
                  ? "bg-red-100 dark:bg-red-950"
                  : isNearLimit
                    ? "bg-amber-100 dark:bg-amber-950"
                    : "bg-gray-200 dark:bg-gray-700"
            }`}
            indicatorClassName={
              isPremium
                ? "bg-gradient-to-r from-amber-500 to-amber-300"
                : isAtLimit
                  ? "bg-red-500"
                  : isNearLimit
                    ? "bg-amber-500"
                    : "bg-blue-500 dark:bg-blue-400"
            }
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className="flex items-center gap-1 text-xss">
              {feature !== "ai_chat" && (
                <>
                  {isPremium ? null : (
                    <span>
                      {current.toLocaleString()} / {max?.toLocaleString()}{" "}
                      {unit && <span className="text-xs ">{unit}</span>}
                    </span>
                  )}
                </>
              )}
            </span>
            <span className="font-satoshi">
              {isPremium ? "Unlimited" : `${percentage}%`}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// LoadingCard skeleton component
function LoadingCard() {
  return (
    <Card className="overflow-hidden relative">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-7 rounded-md" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <Skeleton className="h-2 w-full" />
          <div className="flex justify-between">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-10" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// PremiumBanner component
function PremiumBanner({ onOpenModal }: { onOpenModal: () => void }) {
  return (
    <div className="mt-8 p-5 rounded-lg bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border border-amber-200 dark:border-amber-800/30 transform transition-all duration-200 hover:shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-amber-400/20 mr-4">
            <LucideZap className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h3 className="font-medium">Upgrade to Premium</h3>
            <p className="text-sm text-muted-foreground">
              Unlock unlimited access and advanced features
            </p>
          </div>
        </div>
        <Button
          className="bg-gradient-to-r text-xs from-amber-500 to-amber-300 text-black font-satoshi font-medium hover:from-amber-600 hover:to-amber-400 cursor-pointer transition-all duration-200"
          onClick={onOpenModal}
        >
          View Premium Plans
        </Button>
      </div>
    </div>
  );
}

function UsageStats() {
  const user = useUser();
  const { data, isLoading, error } = useQuery({
    queryKey: ["usage-stats"],
    queryFn: ApiService.getUsageStats,
  });

  const isPremium = !!user?.user_profile.is_premium;

  const showSubscriptionModal = useSubscriptionModal();
  const { toggleSubscriptionModal } = useGlobalStoreActions();

  const handleOpenSubscriptionModal = () => {
    toggleSubscriptionModal(true);
  };

  type Feature =
    | "ai_drafting"
    | "voice_transcription"
    | "ai_chat"
    | "personalization_document"
    | "project_context";

  // Features to display (excluding is_freemium_personalization_used which is handled differently)
  const features: Feature[] = [
    "ai_drafting",
    "voice_transcription",
    "ai_chat",
    "personalization_document",
    "project_context",
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-base font-bold">Your Usage</h1>
        {isPremium && (
          <Badge
            variant="default"
            className="bg-gradient-to-r from-amber-500 to-amber-300 text-black"
          >
            <LucideZap className="h-4 w-4 mr-1" />
            Premium Account
          </Badge>
        )}
      </div>

      {error && (
        <div className="p-4 mb-6 border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800/30 rounded-lg text-red-700 dark:text-red-400">
          Failed to load usage data. Please try again later.
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4].map((i) => (
            <LoadingCard key={i} />
          ))}
        </div>
      ) : data ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => {
              const max = data[feature].max;
              return (
                <UsageCard
                  key={feature}
                  title={featureTitles[feature]}
                  description={featureDescriptions[feature]}
                  current={data[feature].current}
                  max={max}
                  isPremium={isPremium}
                  unit={featureUnits[feature]}
                  feature={feature}
                />
              );
            })}
          </div>

          {!isPremium && (
            <PremiumBanner onOpenModal={handleOpenSubscriptionModal} />
          )}
        </>
      ) : null}

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => toggleSubscriptionModal(false)}
      />
    </div>
  );
}

export default UsageStats;
